import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

interface SearchPanelProps {
  availableTags: string[];
  searching: boolean;
  onSearch: (
    textQuery: string,
    tagQuery: string,
    sourceFilter: string,
    sortOrder: 'newest' | 'oldest'
  ) => void;
  onClearSearch: () => void;
  onSemanticSearch?: (semanticQuery: string) => void;
}

const SearchPanel: React.FC<SearchPanelProps> = ({ 
  availableTags, 
  searching, 
  onSearch, 
  onClearSearch, 
  onSemanticSearch 
}) => {
  const [textSearchQuery, setTextSearchQuery] = useState('');
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');

  const handleSearch = () => {
    onSearch(textSearchQuery, tagSearchQuery, sourceFilter, 'newest');
  };

  const handleClearSearch = () => {
    setTextSearchQuery('');
    setTagSearchQuery('');
    setSourceFilter('all');
    onClearSearch();
  };

  const handleSourceFilterChange = (event: SelectChangeEvent<string>) => {
    setSourceFilter(event.target.value);
  };

  return (
    <Box sx={{ mb: 4, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Filter Q&A Pairs
      </Typography>
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Text Search"
          placeholder="Search questions and answers for exact text"
          value={textSearchQuery}
          onChange={(e) => setTextSearchQuery(e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl fullWidth size="small" variant="outlined">
          <InputLabel id="tag-search-label">Tag Search</InputLabel>
          <Select
            labelId="tag-search-label"
            value={tagSearchQuery}
            onChange={(e) => setTagSearchQuery(e.target.value)}
            label="Tag Search"
            startAdornment={
              <InputAdornment position="start">
                <LocalOfferIcon />
              </InputAdornment>
            }
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {availableTags.map((tag) => (
              <MenuItem key={tag} value={tag}>
                {tag}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth size="small" variant="outlined">
          <InputLabel id="source-filter-label">Source</InputLabel>
          <Select
            labelId="source-filter-label"
            value={sourceFilter}
            onChange={handleSourceFilterChange}
            label="Source"
          >
            <MenuItem value="all">All Sources</MenuItem>
            <MenuItem value="manual">Manual</MenuItem>
            <MenuItem value="webpage">Webpage</MenuItem>
            <MenuItem value="database">Database</MenuItem>
            <MenuItem value="file">File</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button 
          variant="outlined" 
          onClick={handleClearSearch} 
          sx={{ mr: 1 }}
          disabled={searching}
        >
          Clear
        </Button>
        <Button 
          variant="contained"
          onClick={handleSearch}
          startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
          disabled={searching}
        >
          {searching ? 'Searching...' : 'Search'}
        </Button>
      </Box>
    </Box>
  );
};

export default SearchPanel;